import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  getTypographyPreset,
  resolveTypographyStyle,
  type DesignSystemTypography,
} from '../../../lib/typography';
import {
  BUTTON_VISUAL_HEIGHT_PRESETS,
  buttonVisualStateToCssDeclarations,
  buttonVisualStateToReactStyle,
  normalizeButtonDesignStyle,
} from '../../../lib/buttonDesignStyles';
import {
  getButtonVariantClasses,
  resolveButtonVisualVariant,
} from '../../../lib/elementStyleTokens';
import {
  isModelPreviewButtonAction,
  normalizeButtonActionContent,
  resolveButtonHref,
} from '../../../lib/buttonActions';
import { scrollToSectionAnchor } from '../../../lib/sectionAnchors';
import { resolveButtonClickActionTargets } from '../../../lib/buttonEventBridge';
import type { useFlowContext } from '../../flow/FlowProvider';
import { renderSocialIconByLabel } from '../../../lib/socialIcons';
import {
  getHeroButtonSizeClass,
  getHeroButtonStyleClass,
  getHeroFontClass,
} from '../textBlockTypography';

function renderButtonIcon(
  icon: string,
  label: string,
  href: string,
  className = 'h-[1em] w-[1em] shrink-0'
) {
  const normalized = icon.toLowerCase();
  if (
    normalized === 'instagram' ||
    normalized === 'facebook' ||
    normalized === 'mail' ||
    normalized === 'email'
  ) {
    return renderSocialIconByLabel(
      normalized === 'mail' ? 'email' : normalized,
      href,
      className
    );
  }
  if (normalized === 'arrow-right' || normalized === 'arrow') {
    return <span aria-hidden="true">→</span>;
  }
  if (normalized === 'plus') {
    return <span aria-hidden="true">+</span>;
  }
  return renderSocialIconByLabel(label, href, className);
}

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type ModelPreviewState = {
  modelPath: string;
  vaseId?: number;
  title?: string;
};

type ButtonBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: (block: CanvasSectionBlock) => React.ReactNode;
  logicNodeTag: React.ReactNode;
  flow: ReturnType<typeof useFlowContext>;
  sectionId?: string;
  isAdmin: boolean;
  isInteractPreview: boolean;
  isHeroSlotBlock: boolean;
  heroSlotRole: string;
  isSubscribeSlotBlock: boolean;
  subscribeSlotRole: string;
  isEnquirySlotBlock: boolean;
  enquirySlotRole: string;
  typography: Partial<DesignSystemTypography> | undefined;
  fontLibrary: Record<string, string>;
  useFluidSectionTypography: boolean;
  onNavigateToPage?: (href: string, meta?: { pageKey?: string }) => void;
  onOpenModelPreview: (preview: ModelPreviewState) => void;
};

export function ButtonBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  logicNodeTag,
  flow,
  sectionId,
  isAdmin,
  isInteractPreview,
  isHeroSlotBlock,
  heroSlotRole,
  isSubscribeSlotBlock,
  subscribeSlotRole,
  isEnquirySlotBlock,
  enquirySlotRole,
  typography,
  fontLibrary,
  useFluidSectionTypography,
  onNavigateToPage,
  onOpenModelPreview,
}: ButtonBlockRendererProps) {
  const label = String((block.content?.label as string) ?? 'Button');
  const buttonContent =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : {};
  const href = resolveButtonHref(buttonContent);
  const buttonAction = normalizeButtonActionContent(buttonContent);
  const is3DTrigger = isModelPreviewButtonAction(buttonContent);
  const buttonSize = String((block.content?.size as string) ?? '');
  const buttonStyleVariant = String(
    (block.content?.style as string) ?? (block.content?.variant as string) ?? ''
  ).toLowerCase();
  const buttonFont = String((block.content?.font as string) ?? '');
  const buttonIcon = String((block.content?.icon as string) ?? '').toLowerCase();
  const buttonIconPosition = String((block.content?.iconPosition as string) ?? '').toLowerCase();
  const resolvedButtonVariant = resolveButtonVisualVariant(buttonStyleVariant);
  const isPrimaryHeroButton = heroSlotRole !== 'secondary-cta';
  const buttonStyle = resolveTypographyStyle(
    getTypographyPreset(typography, 'button'),
    fontLibrary,
    { fluid: useFluidSectionTypography }
  );
  const buttonDesignStyle = normalizeButtonDesignStyle(block.style);
  const buttonRegularStyle = buttonVisualStateToReactStyle(buttonDesignStyle, fontLibrary);
  const buttonHoverCss = buttonVisualStateToCssDeclarations(buttonDesignStyle.hover, fontLibrary);
  const buttonDomId = `builder-button-${block.id}`;
  const buttonSizingMode = buttonDesignStyle.sizingMode ?? 'stretch';
  const alignX = buttonDesignStyle.alignX ?? 'center';
  const alignY = buttonDesignStyle.alignY ?? 'center';
  const buttonVisualHeight = buttonDesignStyle.visualHeight ?? BUTTON_VISUAL_HEIGHT_PRESETS.default;
  const frameJustifyClass =
    alignX === 'left'
      ? 'justify-start'
      : alignX === 'right'
        ? 'justify-end'
        : 'justify-center';
  const frameAlignClass =
    alignY === 'top'
      ? 'items-start'
      : alignY === 'bottom'
        ? 'items-end'
        : 'items-center';
  const shouldStretchFrameWidth = buttonSizingMode === 'stretch';
  const heroButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    ...buttonRegularStyle,
    transition: 'all 0.2s ease-in-out',
  };
  delete heroButtonStyle.color;
  const baseButtonStyle: React.CSSProperties = isHeroSlotBlock
    ? heroButtonStyle
    : {
        ...buttonStyle,
        ...buttonRegularStyle,
        height: buttonVisualHeight,
        minHeight: buttonVisualHeight,
        maxHeight: buttonVisualHeight,
        transition: 'all 0.2s ease-in-out',
      };

  return (
    <div key={block.id} {...blockWrapperProps}>
      <div className={`relative flex h-full w-full ${frameAlignClass} ${frameJustifyClass}`}>
        {logicNodeTag}
        {buttonHoverCss ? (
          <style>{`[data-builder-button-id="${buttonDomId}"]:hover { ${buttonHoverCss} }`}</style>
        ) : null}
        <a
          href={href}
          data-builder-button-id={buttonDomId}
          data-builder-editor-bounds="content"
          onClick={(event) => {
            const clickTargets = resolveButtonClickActionTargets(flow.flows, {
              blockId: block.id,
              sectionId,
            });
            const hasLogicTargets = Boolean(clickTargets && clickTargets.targetNodeIds.length > 0);

            if (isAdmin && !isInteractPreview) {
              if (is3DTrigger || hasLogicTargets) {
                event.preventDefault();
              }
              return;
            }

            if (is3DTrigger) {
              event.preventDefault();
              if (!buttonAction.modelPath) return;
              onOpenModelPreview({
                modelPath: buttonAction.modelPath,
                vaseId: buttonAction.vaseId,
                title: label,
              });
              return;
            }

            if (buttonAction.mode === 'simple' && buttonAction.kind === 'section-jump') {
              event.preventDefault();
              scrollToSectionAnchor(buttonAction.sectionId);
              return;
            }

            if (!hasLogicTargets || !clickTargets) {
              onNavigateToPage?.(href, {
                pageKey: buttonAction.pageKey,
              });
              return;
            }
            event.preventDefault();
            if (clickTargets.clickFieldId) {
              flow.patchFlowValues(clickTargets.flowId, {
                [clickTargets.clickFieldId]: Date.now(),
              });
            }
            clickTargets.targetNodeIds.forEach((nodeId) => {
              flow.invokeFlowNode(clickTargets.flowId, nodeId);
            });
          }}
          {...(selectionTarget === 'content' ? selectableBlockHitProps : {})}
          className={
            isHeroSlotBlock
              ? `${getHeroButtonSizeClass(buttonSize)} ${getHeroButtonStyleClass(buttonStyleVariant, isPrimaryHeroButton)} ${getHeroFontClass(buttonFont)}`
              : isSubscribeSlotBlock && subscribeSlotRole === 'submit'
                ? 'inline-flex h-full w-full items-center justify-center rounded-md bg-stone-800 px-8 py-4 text-lg font-semibold text-white transition hover:bg-stone-700'
                : isEnquirySlotBlock && enquirySlotRole === 'submit'
                  ? 'inline-flex h-full w-full items-center justify-center rounded-md bg-stone-800 px-8 py-4 text-lg font-semibold text-white transition hover:bg-stone-700'
                  : `inline-flex items-center justify-center gap-1.5 px-3 py-2 transition whitespace-nowrap max-w-full overflow-hidden ${shouldStretchFrameWidth ? 'w-full' : ''} ${getButtonVariantClasses(resolvedButtonVariant)} ${
                      buttonSize === 'lg' ? 'text-base' : buttonSize === 'icon' ? 'px-0 py-0' : ''
                    }`
          }
          style={baseButtonStyle}
        >
          {buttonIcon && buttonIconPosition === 'leading'
            ? renderButtonIcon(buttonIcon, label, href)
            : null}
          {buttonSize === 'icon'
            ? renderButtonIcon(buttonIcon || 'arrow-right', label, href)
            : label}
          {buttonIcon && buttonIconPosition === 'trailing'
            ? renderButtonIcon(buttonIcon, label, href)
            : null}
        </a>
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}
